import { CarePlanNoteProblemResponse } from '~/lib/types/carePlan/carePlans';
import { useCarePlanFrequencyOptions } from '~/lib/hooks/useCarePlanFrequencyOptions';
import { useUpdateProblemMutation } from '~/store/services/carePlan';
import { MultiSelectOption, useToastMessage } from '@equip.health/ui';
import { useEffect, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { SessionGuideEvents } from '~/analytics/sessionGuideEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AnalyticsEvent } from '~/analytics';
import { NOT_YET_ASSESSED_FREQUENCY_CODES } from '~/lib/constants/carePlan.constants';
import { PatientNoteFrequencyRow } from '~/components/maud/patients/note/PatientNoteFrequencyRow';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';

type Props = {
  noteExternalId: string;
  problem: CarePlanNoteProblemResponse;
  treatmentEpisodeId: string;
  showFrequencyErrors: boolean;
};
export const PatientNoteProblemFrequencyRow = ({
  noteExternalId,
  problem,
  treatmentEpisodeId,
  showFrequencyErrors,
}: Props) => {
  const isChanged =
    problem.frequencyHistories?.[0]?.noteExternalId === noteExternalId;

  const isNotYetAssessed = NOT_YET_ASSESSED_FREQUENCY_CODES.includes(
    problem.frequency?.code || '',
  );

  const { cpFrequencyNoteSigningLogic } = useFlags<LDFlags>();
  const { updateFrequencyReadiness, removeFrequencyReadiness } =
    useEditPatientNoteFormContext();
  const { noteId } = useEditNoteDrawerContext();
  const { trackEvent } = useAnalytics();

  const sendAnalyticsEvent = (analyticsEvent: AnalyticsEvent) => {
    trackEvent(analyticsEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
  };
  const {
    options: frequencyOptions,
    placeholder: frequencyPlaceholder,
    isLoading: isGetFrequenciesLoading,
  } = useCarePlanFrequencyOptions({
    frequencyCode: problem.frequency?.code,
    isNoteContext: true,
  });
  const [updateProblem, { error }] = useUpdateProblemMutation();
  const { createToast } = useToastMessage();

  const isReadyForSigning = useMemo(() => {
    // If the parent goal is NOT_ASSESSED, this frequency is automatically ready
    const goal = problem.goals[0];
    if (goal && goal.status.code === CarePlanGoalStatus.NOT_ASSESSED) {
      return true;
    }

    if (cpFrequencyNoteSigningLogic && problem.frequency?.required === false) {
      return true;
    }

    if (!problem.frequencyHistories?.length) {
      return false;
    }

    return problem.frequencyHistories[0].noteExternalId === noteId;
  }, [
    cpFrequencyNoteSigningLogic,
    problem.frequency,
    problem.frequencyHistories,
    problem.goals,
    noteId,
  ]);

  // Register this frequency with the context
  useEffect(() => {
    updateFrequencyReadiness(problem.id, isReadyForSigning);
    return () => {
      removeFrequencyReadiness(problem.id);
    };
  }, [
    problem.id,
    isReadyForSigning,
    updateFrequencyReadiness,
    removeFrequencyReadiness,
  ]);

  useEffect(() => {
    if (error) {
      createToast({
        id: 'patient-note-problem-frequency-row-update-error',
        text: 'An error occurred updating the frequency. Please try again later.',
        variant: 'critical',
      });
    }
  }, [error, createToast]);

  const callUpdateProblem = (frequencyCode: string) => {
    updateProblem({
      body: {
        manifestations: [], // this component is only loaded when manifestations are empty
        noteExternalId,
        frequencyCode,
      },
      problemId: problem.id,
      treatmentEpisodeId,
    });
  };

  const handleConfirm = () => {
    sendAnalyticsEvent(SessionGuideEvents.sessionGuideFrequencyConfirmed);
    if (problem.frequency?.code) {
      callUpdateProblem(problem.frequency.code);
    }
  };

  const handleFrequencyChange = (newFrequency: MultiSelectOption) => {
    sendAnalyticsEvent(SessionGuideEvents.sessionGuideFrequencyOptionSelected);
    callUpdateProblem(newFrequency.value);
  };

  const showError = showFrequencyErrors && !isReadyForSigning;

  return (
    <PatientNoteFrequencyRow
      id={problem.id}
      isButtonDisabled={isNotYetAssessed}
      isChanged={isChanged}
      isLoading={isGetFrequenciesLoading}
      isRequired={
        cpFrequencyNoteSigningLogic &&
        (problem.frequency === null || problem.frequency?.required)
      }
      label={
        problem.pathwayProblem.providerFacingLanguage ||
        problem.pathwayProblem.label
      }
      onChange={handleFrequencyChange}
      onConfirm={handleConfirm}
      onMenuOpen={() =>
        sendAnalyticsEvent(
          SessionGuideEvents.sessionGuideFrequencyDropdownClicked,
        )
      }
      options={frequencyOptions}
      placeholder={frequencyPlaceholder}
      showError={showError}
      value={frequencyOptions.find(
        (option) => option.value === problem.frequency?.code,
      )}
      frequencyHistories={problem.frequencyHistories}
      isReadyForSigning={isReadyForSigning}
    />
  );
};
